import styled from "styled-components";
import {
  IdentificationDocumentData,
  LandingPageProxyPassportFormPassenger,
} from "../../../types/IdentificationForm";
import React, { useCallback, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { IdentificationFormStep } from "../../../enums/IdentificationFormPath";
import Button from "../../../components/TixUiButton";
import { Breakpoint } from "../../../enums/Breakpoints";
import useCountryValues from "../../../hooks/useCountryNames";
import { format as formatDate } from "date-fns";
import {
  de,
  el,
  enGB,
  es,
  fi,
  fr,
  it,
  ko,
  nb,
  nl,
  pl,
  sv,
  tr,
} from "date-fns/locale";
import { useConfig } from "../../../contexts";
import Edit from "../../../icons/Edit";
import { useTranslationV3 } from "../../../i18nV3/useTranslationV3";
import { saveIdentificationDocuments } from "../../../api/identificationFormActions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";

let locales: { [key: string]: Locale } = {
  de,
  el,
  enGB,
  es,
  fi,
  fr,
  it,
  ko,
  nb,
  nl,
  pl,
  sv,
  tr,
};

interface Props {
  passengers: LandingPageProxyPassportFormPassenger[];
  values: IdentificationDocumentData[];
  setErrorSource: (value: "generic" | "410") => void;
  setFormSuccessfullySent: (wasFormSuccessfullySent: boolean) => void;
}

const SecondStep = ({
  passengers,
  values,
  setErrorSource,
  setFormSuccessfullySent,
}: Props) => {
  const { t } = useTranslationV3();
  const { countries } = useCountryValues();
  const history = useHistory();
  const params = useParams<{ tripId: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const config = useConfig();

  const goToFillData = useCallback(() => {
    history.push(
      `/identification-form/${params.tripId}/${IdentificationFormStep.FillData}`
    );
  }, [history, params]);

  const sendIdentificationData = useCallback(async () => {
    try {
      setIsLoading(true);
      await saveIdentificationDocuments(params.tripId, values);
      setFormSuccessfullySent(true);
    } catch (error: any) {
      error.response?.status === 410
        ? setErrorSource("410")
        : setErrorSource("generic");
    } finally {
      setIsLoading(false);
    }
  }, [setErrorSource, setFormSuccessfullySent, values, params.tripId]);

  return (
    <>
      {passengers.map((passenger, index) => (
        <PassengerContainer key={index}>
          <LeftBox>
            <BoldText>{`${passenger.firstName} ${passenger.lastName}`}</BoldText>
            <NormalText>
              {t(`gender_${passenger.gender[0].toLowerCase()}`)}
            </NormalText>
            <NormalText>{`${formatDate(
              new Date(passenger.dateOfBirth),
              "dd MMMM yyyy",
              {
                locale: locales[config.defaultLang] || locales.enGB,
              }
            )}`}</NormalText>
          </LeftBox>
          <RightBox>
            <FirstRow>
              <NormalText>
                {t(`tix_ui_id_type_${values[index].idType}`)}
              </NormalText>
              <EditButton onClick={() => goToFillData()}>
                {t("tix_ui_identification_form_step_2_edit")} <Edit />
              </EditButton>
            </FirstRow>
            <NormalText>
              {countries.getName(
                values[index].issueCountry,
                config.defaultLang
              )}
            </NormalText>
            <NormalText>{values[index].idNumber}</NormalText>
            <NormalText>{values[index].dateOfExpiry}</NormalText>
          </RightBox>
        </PassengerContainer>
      ))}
      <ButtonContainer>
        <TransparentButton
          disabled={isLoading}
          isLoading={isLoading}
          color="transparent"
          bordered
          onClick={goToFillData}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
          {t("tix_ui_identification_form_step_2_back")}
        </TransparentButton>
        <StyledButton
          disabled={isLoading}
          isLoading={isLoading}
          onClick={sendIdentificationData}
        >
          {t("tix_ui_identification_form_step_2_submit")}
        </StyledButton>
      </ButtonContainer>
    </>
  );
};

const FirstRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const EditButton = styled.span`
  font-size: 14px;
  font-family: ${({ theme }) => theme.redesign.regularFont};
  text-decoration: underline;
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    margin-left: 6px;
    height: 15px;
    width: 15px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
`;

const TransparentButton = styled(Button)`
  min-width: 97px;
  width: fit-content;
  height: 48px;
  font-size: 14px;
  font-family: ${({ theme }) => theme.redesign.regularFont};
  margin-right: 20px;

  svg {
    margin-right: 15px;
  }
`;

const StyledButton = styled(Button)`
  min-width: 275px;
  width: fit-content;

  @media ${Breakpoint.MaxMobile} {
    min-width: 218px;
  }
`;

const PassengerContainer = styled.div`
  display: flex;
  border-top: 1px solid ${({ theme }) => theme.redesign.defaultColor};
  padding: 40px 0;

  @media ${Breakpoint.MaxMobile} {
    flex-direction: column;
  }
`;

const LeftBox = styled.div`
  width: 220px;
  background-color: ${({ theme }) => theme.redesign.defaultBackground};
  padding: 20px;
  margin-right: 20px;
  flex: 1;

  @media ${Breakpoint.MaxMobile} {
    width: calc(100% - 40px);
    margin-right: 0px;
    margin-bottom: 10px;
  }
`;

const RightBox = styled.div`
  background-color: ${({ theme }) => theme.redesign.defaultBackground};
  padding: 20px;
  width: calc(100% - 280px);
  @media ${Breakpoint.MaxMobile} {
    width: calc(100% - 40px);
  }
`;

const BoldText = styled.div`
  font-family: ${({ theme }) => theme.redesign.heavyFont};
  font-size: 14px;
  line-height: 15px;
  &:not(:last-child) {
    padding-bottom: 5px;
  }
`;

const NormalText = styled.div`
  font-family: ${({ theme }) => theme.redesign.regularFont};
  font-size: 14px;
  line-height: 15px;
  &:not(:last-child) {
    padding-bottom: 5px;
  }
`;

export default SecondStep;
